import React from 'react';
import { Flex } from '@chakra-ui/react';
import { useFormContext } from 'react-hook-form';

import { PasswordPolicy } from 'types/auth';
import { PasswordPolicyMessage } from 'app/components/Core/PasswordPolicyMessage';
import { SignUpFormFields } from 'app/components/SignUp/SignUpForm/types';
import { InputField } from 'app/components/Core/ChakraInputField';
import { HelpTooltip } from 'app/components/HelpTooltip';
import { AuthFormHeader } from 'app/components/AuthForm';
import { PasswordInput } from '../../Core';

interface SignUpBasicFormProps {
  passwordPolicy?: PasswordPolicy;
  isUBC?: boolean;
  emailParamExists?: boolean;
}

export const SignUpBasicForm: React.FC<SignUpBasicFormProps> = ({
  passwordPolicy,
  isUBC,
  emailParamExists,
}) => {
  const {
    formState: { errors },
    register,
    getValues,
    trigger,
    clearErrors,
  } = useFormContext<SignUpFormFields>();

  const onFocusEmail = () => {
    const emailError = errors?.email;
    if (emailError?.type === 'manual') {
      clearErrors('email');
    }
  };

  return (
    <Flex direction="column">
      <AuthFormHeader heading="Create an account" mb={12}>
        Please choose you email and password.
      </AuthFormHeader>
      <InputField
        label="Email"
        isDisabled={isUBC && emailParamExists}
        isInvalid={Boolean(errors.email)}
        errorMessage={errors.email?.message}
        data-testid="email"
        rightElement={
          isUBC ? (
            <HelpTooltip label="Join Session Studio with your UBC e-mail to link your UBC IDs. You can change your e-mail address after you join." />
          ) : undefined
        }
        onFocus={onFocusEmail}
        {...register('email')}
      />
      <PasswordInput
        label="Password"
        autoComplete="new-password"
        {...register('password', {
          onBlur: () => {
            if (errors.password?.type === 'manual') {
              clearErrors();
            }
          },
          onChange: () => {
            if (getValues('passwordRepeat')) {
              trigger('passwordRepeat');
            }
          },
        })}
        isInvalid={Boolean(errors?.password)}
        errorMessage={errors?.password?.message}
      />
      <PasswordInput
        label="Confirm Password"
        autoComplete="new-password"
        {...register('passwordRepeat', {
          onBlur: () => {
            if (errors.password?.type === 'manual') {
              clearErrors();
            }
          },
        })}
        isInvalid={Boolean(errors?.passwordRepeat)}
        errorMessage={errors?.passwordRepeat?.message}
        data-testid="confirm-password"
        errorMessageProps={{
          'data-testid': 'confirm-password-error-text',
        }}
      />
      <PasswordPolicyMessage passwordPolicy={passwordPolicy} />
    </Flex>
  );
};
